import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Check, X, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { TESTIMONIAL_TABS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const EMPTY_FORM = {
    category: "patient",
    text: "",
    name: "",
    country: "",
    role: "",
    organisation: "",
    image_url: "",
    attribution_approved: false,
    status: "pending",
};

export default function Admin() {
    const navigate = useNavigate();
    const [token, setToken] = useState(() => localStorage.getItem("vm_admin_token") || null);
    const [checking, setChecking] = useState(!!localStorage.getItem("vm_admin_token"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [items, setItems] = useState([]);
    const [view, setView] = useState("testimonials");
    const [enquiries, setEnquiries] = useState([]);
    const [active, setActive] = useState("patient");
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/auth/me`, auth)
            .then(() => {
                setChecking(false);
                loadItems();
            })
            .catch(() => {
                localStorage.removeItem("vm_admin_token");
                setToken(null);
                setChecking(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const loadItems = async () => {
        try {
            const r = await axios.get(`${API}/admin/testimonials`, auth);
            setItems(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const loadEnquiries = async () => {
        try {
            const r = await axios.get(`${API}/admin/enquiries`, auth);
            setEnquiries(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const setEnquiryStatus = async (id, status) => {
        try {
            await axios.put(`${API}/admin/enquiries/${id}`, { status }, auth);
            loadEnquiries();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const viewEnquiryFile = async (en) => {
        try {
            const r = await axios.get(`${API}/admin/enquiries/${en.id}/file`, {
                ...auth,
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open attachment");
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem("vm_admin_token", data.access_token);
            setToken(data.access_token);
            toast.success("Signed in");
        } catch (e2) {
            setError(formatApiErrorDetail(e2.response?.data?.detail));
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("vm_admin_token");
        setToken(null);
        setItems([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            name: form.name || null,
            country: form.country || null,
            role: form.role || null,
            organisation: form.organisation || null,
        };
        try {
            if (editingId) {
                await axios.put(`${API}/admin/testimonials/${editingId}`, payload, auth);
                toast.success("Review updated");
            } else {
                await axios.post(`${API}/admin/testimonials`, payload, auth);
                toast.success("Review added");
            }
            setForm(EMPTY_FORM);
            setEditingId(null);
            setShowForm(false);
            loadItems();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        }
    };

    const setStatus = async (id, status) => {
        try {
            await axios.put(`${API}/admin/testimonials/${id}`, { status }, auth);
            toast.success(status === "approved" ? "Review approved and published" : "Review unpublished");
            loadItems();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this review permanently?")) return;
        try {
            await axios.delete(`${API}/admin/testimonials/${id}`, auth);
            toast.success("Review removed");
            loadItems();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const startEdit = (t) => {
        setForm({
            category: t.category,
            text: t.text,
            name: t.name || "",
            country: t.country || "",
            role: t.role || "",
            organisation: t.organisation || "",
            image_url: t.image_url || "",
            attribution_approved: !!t.attribution_approved,
            status: t.status,
        });
        setEditingId(t.id);
        setShowForm(true);
    };

    const aliasFor = (category) => TESTIMONIAL_TABS.find((t) => t.id === category)?.alias || "Verified";
    const list = items.filter((i) => i.category === active);

    const inputCls =
        "w-full border border-white/15 bg-navy-deep px-4 py-3 text-sm text-ivory placeholder:text-slate-500 focus:border-gold focus:outline-none";

    if (checking) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-navy-deep" data-testid="admin-loading">
                <span className="text-xs uppercase tracking-[0.3em] text-gold">Checking session…</span>
            </div>
        );
    }

    if (!token) {
        return (
            <div className="grain flex min-h-screen items-center justify-center bg-navy-deep px-5" data-testid="admin-login-page">
                <div className="w-full max-w-md border border-white/10 bg-navy-surface p-8 lg:p-10">
                    <span className="inline-flex rounded-[3px] bg-ivory px-3 py-2">
                        <img src="/assets/logos/viet-medica-logo.png" alt="Viet Medica Ltd — official logo" className="h-8 w-auto" />
                    </span>
                    <h1 className="mt-7 font-serif text-3xl font-light text-ivory">Testimonial Manager</h1>
                    <p className="mt-2 text-sm text-slate-400">Authorised administrators only.</p>
                    <form onSubmit={handleLogin} className="mt-8 space-y-4">
                        <div>
                            <label htmlFor="admin-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                                Email
                            </label>
                            <input
                                id="admin-email"
                                data-testid="admin-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputCls}
                                placeholder="admin@vietmedica.com"
                            />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                                Password
                            </label>
                            <input
                                id="admin-password"
                                data-testid="admin-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={inputCls}
                                placeholder="••••••••"
                            />
                        </div>
                        {error && (
                            <p className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="admin-login-error">
                                {error}
                            </p>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            className="w-full bg-gold px-6 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-navy-deep transition-colors hover:bg-gold-bright"
                        >
                            Sign In
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500 transition-colors hover:text-gold"
                        >
                            <ArrowLeft size={13} /> Back to website
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen bg-navy-deep" data-testid="admin-dashboard">
            <header className="border-b border-white/10 bg-navy-deep/90">
                <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-4">
                        <span className="inline-flex rounded-[3px] bg-ivory px-3 py-2">
                            <img src="/assets/logos/viet-medica-logo.png" alt="Viet Medica Ltd" className="h-7 w-auto" />
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">
                            Testimonial Manager
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="text-xs text-slate-400 transition-colors hover:text-gold"
                        >
                            View website
                        </button>
                        <button
                            type="button"
                            data-testid="admin-logout-button"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <LogOut size={13} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1200px] px-5 py-10">
                <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-view-toggle">
                    {[
                        ["testimonials", "Testimonials"],
                        ["enquiries", "Enquiries"],
                    ].map(([v, label]) => (
                        <button
                            key={v}
                            type="button"
                            data-testid={`admin-view-${v}`}
                            onClick={() => {
                                setView(v);
                                if (v === "enquiries") loadEnquiries();
                            }}
                            className={`border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-colors ${
                                view === v
                                    ? "border-gold bg-gold/10 text-gold"
                                    : "border-white/10 text-slate-400 hover:border-gold/40 hover:text-ivory"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {view === "enquiries" ? (
                    <EnquiryPanel enquiries={enquiries} onStatus={setEnquiryStatus} />
                ) : (
                <>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                        {TESTIMONIAL_TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                data-testid={`admin-tab-${t.id}`}
                                onClick={() => setActive(t.id)}
                                className={`border px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
                                    active === t.id
                                        ? "border-gold bg-gold/10 text-gold"
                                        : "border-white/10 text-slate-400 hover:border-gold/40 hover:text-ivory"
                                }`}
                            >
                                {t.label} ({items.filter((i) => i.category === t.id).length})
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-testid="admin-add-review-button"
                        onClick={() => {
                            setForm({ ...EMPTY_FORM, category: active });
                            setEditingId(null);
                            setShowForm((v) => !v);
                        }}
                        className="inline-flex items-center gap-2 bg-gold px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-navy-deep transition-colors hover:bg-gold-bright"
                    >
                        <Plus size={14} /> {showForm ? "Close" : "Add Review"}
                    </button>
                </div>

                {showForm && (
                    <form
                        onSubmit={handleSubmit}
                        className="mt-8 border border-gold/25 bg-navy-surface p-7"
                        data-testid="admin-review-form"
                    >
                        <h2 className="font-serif text-2xl font-light text-ivory">
                            {editingId ? "Edit Review" : "Add Genuine Review"}
                        </h2>
                        <p className="mt-2 text-xs leading-relaxed text-slate-500">
                            Publish only real reviews supplied by clients or partners. Attribution details are
                            shown publicly only when permission is confirmed.
                        </p>
                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label htmlFor="rev-category" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                    Category
                                </label>
                                <select
                                    id="rev-category"
                                    data-testid="review-category-select"
                                    value={form.category}
                                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    className={inputCls}
                                >
                                    {TESTIMONIAL_TABS.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="rev-status" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                    Status
                                </label>
                                <select
                                    id="rev-status"
                                    data-testid="review-status-select"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="pending">Pending — hidden from website</option>
                                    <option value="approved">Approved — visible on website</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4">
                            <label htmlFor="rev-text" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                Review text (exact wording as provided)
                            </label>
                            <textarea
                                id="rev-text"
                                data-testid="review-text-input"
                                required
                                rows={4}
                                value={form.text}
                                onChange={(e) => setForm({ ...form, text: e.target.value })}
                                className={inputCls}
                                placeholder="Paste the genuine review exactly as provided…"
                            />
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {[
                                ["name", "Name (optional)", "review-name-input"],
                                ["country", "Country (optional)", "review-country-input"],
                                ["role", "Role (optional)", "review-role-input"],
                                ["organisation", "Organisation (optional)", "review-organisation-input"],
                                ["image_url", "Photo or logo URL (only with written permission)", "review-image-input"],
                            ].map(([key, label, tid]) => (
                                <div key={key}>
                                    <label htmlFor={`rev-${key}`} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                        {label}
                                    </label>
                                    <input
                                        id={`rev-${key}`}
                                        data-testid={tid}
                                        type="text"
                                        value={form[key]}
                                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                            ))}
                        </div>
                        <label className="mt-5 flex items-start gap-3 text-sm text-slate-300" htmlFor="rev-permission">
                            <input
                                id="rev-permission"
                                data-testid="review-permission-checkbox"
                                type="checkbox"
                                checked={form.attribution_approved}
                                onChange={(e) => setForm({ ...form, attribution_approved: e.target.checked })}
                                className="mt-1 h-4 w-4 accent-[#C5A059]"
                            />
                            Written permission confirmed to display the name, role, organisation and country
                            publicly. Without this, the review appears as a verified anonymous attribution.
                        </label>
                        <div className="mt-6 flex gap-3">
                            <button
                                type="submit"
                                data-testid="review-save-button"
                                className="bg-gold px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors hover:bg-gold-bright"
                            >
                                {editingId ? "Save Changes" : "Add Review"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingId(null);
                                    setForm(EMPTY_FORM);
                                }}
                                className="border border-white/15 px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-8 space-y-4" data-testid="admin-review-list">
                    {list.length === 0 && (
                        <div className="border border-dashed border-white/12 p-10 text-center">
                            <p className="text-sm text-slate-400">
                                No reviews in this category yet. Nothing is shown on the public website until a
                                genuine review is added and approved.
                            </p>
                        </div>
                    )}
                    {list.map((t) => (
                        <article
                            key={t.id}
                            className="border border-white/8 bg-navy-surface p-6"
                            data-testid={`admin-review-${t.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="max-w-3xl">
                                    <p className="text-sm leading-relaxed text-slate-200/90">&ldquo;{t.text}&rdquo;</p>
                                    <p className="mt-3 text-xs text-slate-500">
                                        {t.attribution_approved && t.name
                                            ? [t.name, t.role, t.organisation, t.country].filter(Boolean).join(" · ")
                                            : `Shown publicly as "${aliasFor(t.category)}"`}
                                    </p>
                                </div>
                                <span
                                    className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${
                                        t.status === "approved"
                                            ? "border-tealr/50 text-tealr"
                                            : "border-gold/50 text-gold"
                                    }`}
                                    data-testid={`admin-review-status-${t.id}`}
                                >
                                    {t.status === "approved" ? "Published" : "Pending"}
                                </span>
                            </div>
                            <div className="mt-5 flex flex-wrap gap-2 border-t border-white/8 pt-4">
                                {t.status === "approved" ? (
                                    <button
                                        type="button"
                                        data-testid={`admin-unapprove-${t.id}`}
                                        onClick={() => setStatus(t.id, "pending")}
                                        className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                                    >
                                        <X size={13} /> Unpublish
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        data-testid={`admin-approve-${t.id}`}
                                        onClick={() => setStatus(t.id, "approved")}
                                        className="inline-flex items-center gap-2 border border-tealr/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-tealr transition-colors hover:bg-tealr hover:text-navy-deep"
                                    >
                                        <Check size={13} /> Approve &amp; Publish
                                    </button>
                                )}
                                <button
                                    type="button"
                                    data-testid={`admin-edit-${t.id}`}
                                    onClick={() => startEdit(t)}
                                    className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                                >
                                    <Pencil size={13} /> Edit
                                </button>
                                <button
                                    type="button"
                                    data-testid={`admin-remove-${t.id}`}
                                    onClick={() => remove(t.id)}
                                    className="inline-flex items-center gap-2 border border-red-500/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-red-400 transition-colors hover:bg-red-500/10"
                                >
                                    <Trash2 size={13} /> Remove
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
                </>
                )}
            </main>
        </div>
    );
}

const ENQUIRY_CATEGORY_LABELS = {
    healthcare: "Healthcare",
    trading: "Trading",
    sourcing: "Vietnam Sourcing",
    partnership: "Partnership",
    general: "General",
};

const ENQUIRY_STATUS = {
    new: { label: "New", cls: "border-gold/50 text-gold" },
    "in-progress": { label: "In Progress", cls: "border-tealr/50 text-tealr" },
    closed: { label: "Closed", cls: "border-white/20 text-slate-400" },
};

function formatKey(k) {
    return k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

function EnquiryPanel({ enquiries, onStatus }) {
    return (
        <div className="space-y-4" data-testid="admin-enquiry-list">
            {enquiries.length === 0 && (
                <div className="border border-dashed border-white/12 p-10 text-center">
                    <p className="text-sm text-slate-400">
                        No enquiries yet. Submitted website forms appear here, routed by category.
                    </p>
                </div>
            )}
            {enquiries.map((en) => (
                <article
                    key={en.id}
                    className="border border-white/8 bg-navy-surface p-6"
                    data-testid={`admin-enquiry-${en.id}`}
                >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="border border-gold/50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                                {ENQUIRY_CATEGORY_LABELS[en.category] || en.category}
                            </span>
                            <span className="text-xs text-slate-500">{String(en.created_at || "").slice(0, 10)}</span>
                            {en.file_name && (
                                <button
                                    type="button"
                                    onClick={() => viewEnquiryFile(en)}
                                    data-testid={`admin-enquiry-file-${en.id}`}
                                    className="text-xs text-gold underline underline-offset-2 hover:opacity-80"
                                >
                                    Attachment: {en.file_name}
                                </button>
                            )}
                        </div>
                        <span
                            className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${
                                ENQUIRY_STATUS[en.status]?.cls || ""
                            }`}
                        >
                            {ENQUIRY_STATUS[en.status]?.label || en.status}
                        </span>
                    </div>
                    <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(en.data || {})
                            .filter(([, v]) => v !== "" && v != null && v !== false)
                            .map(([k, v]) => (
                                <div key={k}>
                                    <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        {formatKey(k)}
                                    </dt>
                                    <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-200">{String(v)}</dd>
                                </div>
                            ))}
                    </dl>
                    <div className="mt-5 flex flex-wrap gap-2 border-t border-white/8 pt-4">
                        {en.status !== "in-progress" && (
                            <button
                                type="button"
                                data-testid={`enquiry-progress-${en.id}`}
                                onClick={() => onStatus(en.id, "in-progress")}
                                className="border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                            >
                                Mark In Progress
                            </button>
                        )}
                        {en.status !== "closed" && (
                            <button
                                type="button"
                                data-testid={`enquiry-close-${en.id}`}
                                onClick={() => onStatus(en.id, "closed")}
                                className="border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                            >
                                Close
                            </button>
                        )}
                        {en.status === "closed" && (
                            <button
                                type="button"
                                data-testid={`enquiry-reopen-${en.id}`}
                                onClick={() => onStatus(en.id, "new")}
                                className="border border-gold/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold"
                            >
                                Reopen
                            </button>
                        )}
                    </div>
                </article>
            ))}
        </div>
    );
}
